import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress, Typography } from '@mui/material';

// Import pages
import HomePage from './pages/HomePage';
import LeaguePage from './pages/LeaguePage';
import MyTeamPage from './pages/MyTeamPage';
import PlayersPage from './pages/Players2Page';
import ScoresPage from './pages/ScoresPage';
import DraftPage from './pages/DraftPage';
import SettingsPage from './pages/SettingsPage';
import MatchupPage from './pages/MatchupPage';
import StatsPage from './pages/StatsPage';
import SchedulePage from './pages/SchedulePage';

// Import navigation
import Navigation from './components/Navigation';

// Import user switcher
import UserSwitcher from './components/UserSwitcher';

// Import password gate
import PasswordGate from './components/PasswordGate';

// Import AdSense
import { AdSenseBanner } from './components/AdSenseBanner';
import { AD_SENSE_CONFIG } from './config/adsense';

// Import user context
import { UserProvider, useUser } from './contexts/UserContext';
// Import player context and dialog
import { PlayerProvider } from './contexts/PlayerContext';
import PlayerDialog from './components/PlayerDialog';
// Import page content context
import { PageContentProvider, usePageContent } from './contexts/PageContentContext';

// Create theme that matches your MAUI app colors
const theme = createTheme({
  palette: {
    primary: {
      main: '#FF6B35',
      dark: '#E55A2B',
    },
    secondary: {
      main: '#2C3E50',
    },
    success: {
      main: '#27AE60',
    },
    warning: {
      main: '#F39C12',
    },
    error: {
      main: '#E74C3C',
    },
    info: {
      main: '#3498DB',
    },
    background: {
      default: '#F8F9FA',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#2C3E50',
      secondary: '#7F8C8D',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(31, 41, 55, 0.1)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
  },
});

// Route wrapper to reset page content state on route changes
const RouteWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { resetPageState } = usePageContent();
  
  React.useEffect(() => {
    // Reset page state when route changes
    resetPageState();
  }, [location.pathname, resetPageState]);
  
  return <>{children}</>;
};

// Memoize the routes to prevent unnecessary re-renders
const AppRoutes = React.memo(() => {
  const location = useLocation();
  const { resetPageState } = usePageContent();
  
  // Reset page state when route changes
  React.useEffect(() => {
    resetPageState();
  }, [location.pathname, resetPageState]);
  
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/myteam" element={<MyTeamPage />} />
      <Route path="/players" element={<PlayersPage />} />
      <Route path="/scores" element={<ScoresPage />} />
      <Route path="/league" element={<LeaguePage />} />
      <Route path="/matchup" element={<MatchupPage />} />
      <Route path="/draft" element={<DraftPage />} />
      <Route path="/stats" element={<StatsPage />} />
      <Route path="/settings" element={<SettingsPage />} />
      <Route path="/schedule" element={<SchedulePage />} />
    </Routes>
  );
});

AppRoutes.displayName = 'AppRoutes';

// Separate loading overlay component to prevent hook unmounting
const LoadingOverlay: React.FC = () => {
  const { currentUsername } = useUser();
  
  return (
    <Box sx={{ 
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      zIndex: 9999
    }}>
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Switching to {currentUsername}...
        </Typography>
      </Box>
    </Box>
  );
};

// Main app content - keep this stable to prevent hook unmounting
const AppContent: React.FC = () => {
  const { isUserSwitching } = useUser();
  const { hasContent, isLoading, hasError } = usePageContent();
  const location = useLocation();
  const [showAds, setShowAds] = React.useState(false);
  
  // Hide ads on route change and show them after a delay to ensure page has content
  // This prevents ads from showing on loading/error states (low-value content screens)
  React.useEffect(() => {
    // Hide ads immediately when route changes
    setShowAds(false);
    
    // Only show ads if not switching users
    if (!isUserSwitching) {
      // Delay showing ads to allow pages to render and determine if they have content
      // Pages that return early with loading/error states won't trigger this delay
      const timer = setTimeout(() => {
        setShowAds(true);
      }, 1500); // 1.5 second delay to ensure page has rendered content
      
      return () => clearTimeout(timer);
    } else {
      // Keep ads hidden while switching users
      setShowAds(false);
    }
  }, [location.pathname, isUserSwitching]);
  
  // Determine if ads should be shown based on page content state
  // Per Google AdSense policy: no ads on screens without content, loading states, or error pages
  const shouldShowAds = !isUserSwitching && 
                        showAds && 
                        hasContent && 
                        !isLoading && 
                        !hasError &&
                        AD_SENSE_CONFIG.TOP_BANNER_SLOT !== 'YOUR_AD_SLOT_ID_HERE';
  
  return (
    <>
      <Navigation />
      <UserSwitcher />
      {/* AdSense Banner - only show when page has content and not loading/switching */}
      {/* Per Google AdSense policy: no ads on screens without content, loading states, or error pages */}
      {shouldShowAds && (
        <AdSenseBanner 
          adSlot={AD_SENSE_CONFIG.TOP_BANNER_SLOT} 
          spacing={1}
          sx={{ 
            backgroundColor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            py: 1
          }}
        />
      )}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <AppRoutes />
      </Box>
      {isUserSwitching && <LoadingOverlay />}
    </>
  );
};

AppContent.displayName = 'AppContent';

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <PasswordGate>
        <UserProvider>
          <PlayerProvider>
            <PageContentProvider>
              <Router>
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                  <AppContent />
                  <PlayerDialog />
                </Box>
              </Router>
            </PageContentProvider>
          </PlayerProvider>
        </UserProvider>
      </PasswordGate>
    </ThemeProvider>
  );
};

export default App;
